/* eslint-disable no-plusplus */
/* eslint-disable no-unsafe-optional-chaining */
import { GridColDef } from "@mui/x-data-grid-pro";
import {
  Card,
  Divider,
  FilterIcon,
  GKButton,
  GKDataGrid,
  Grid2,
  IconButton,
  NoData,
  ReloadIcon,
  RightIcon,
  Tab,
  Tabs,
  Typography,
} from "components";
import _ from "lodash";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  LARGE_CAP_COLOR,
  MID_CAP_COLOR,
  PieChartColors,
  SMALL_CAP_COLOR,
} from "utils/constants";
import { extraColumnsField } from "utils/helpers";
import MFSchemeWeightage from "./MFSchemeWeightage";
import PairwiseSchemeOverlap from "./PairwiseSchemeOverlap";
import PieChartSection from "./PieChartSection";
import SunburstSection from "./SunburstSection";

interface InsigntsPageContentProps {
  allHoldingsInsightsData?: any[];
  isLoading: boolean;
  onRowClick?: (row: any) => void;
  selectedItems: any[];
  resetOverlapKey: any;
  setIsModalOpen?: (open: boolean) => void;
}

const InsigntsPageContent = (props: InsigntsPageContentProps) => {
  const {
    allHoldingsInsightsData,
    isLoading = false,
    // onRowClick,
    // selectedItems,
    resetOverlapKey,
    setIsModalOpen,
  } = props;

  const [pieClickData, setPieClickData] = useState<any>();

  const [currentValueTabs, setCurrentValueTabs] = useState(0);

  const [sunBurstClickData, setSunBurstClickData] = useState<any>();

  const [sunBurstHoverData, setSunBurstHoverData] = useState<any>();

  const [pieHoverData, setPieHoverData] = useState<any>();

  const sunBurstRef = useRef(null);

  const pieRef = useRef(null);

  const [pieData, setPieData] = useState<any>([]);

  const [sunBurstData, setSunBurstData] = useState<any>([]);

  const [capSizes, setCapSizes] = useState<any>([]);

  // const requestBody = useGenerateQueryParams("insights", {
  //   portfolios: JSON.stringify(
  //     selectedItems
  //       ?.map((data: any) => data?.portfolios)
  //       ?.flat()
  //       ?.map((dataItem) => dataItem?.value),
  //   ),
  // });

  // const { data: holdingData, isFetching: holdingLoading } = useFetchQuery({
  //   key: [
  //     "INSIGHTS",
  //     requestBody,
  //     selectedItems
  //       ?.map((data: any) => data?.portfolios)
  //       ?.flat()
  //       ?.map((dataItem) => dataItem?.value),
  //   ],
  //   retry: 0,
  //   route: GET_HOLDING,
  //   requestBody,
  // });

  const checkCurrentValue = (sum: any, current: any) =>
    currentValueTabs === 0
      ? sum + current?.currentAmount
      : sum + current?.investedAmount;

  const totalInvestment = allHoldingsInsightsData?.reduce(
    (sum: any, current: any) => checkCurrentValue(sum, current),
    0,
  );

  const sectorsArray = (useInstType = false) =>
    allHoldingsInsightsData.map((item: any) => ({
      sector: useInstType ? item.instType : item.asset.sector,
      companyName: item.asset.name,
      investedAmount: item.investedAmount,
      companyPercentage:
        currentValueTabs === 0
          ? (item.currentAmount / totalInvestment) * 100
          : (item.investedAmount / totalInvestment) * 100,
    }));

  const mfHoldingsData = useMemo(() => {
    return allHoldingsInsightsData?.filter(
      (item: any) => item.instType === "MF",
    );
  }, [allHoldingsInsightsData]);

  const mfSchemeColumns: GridColDef[] = [
    {
      ...extraColumnsField("Scheme Name", "schemeName", 180, 1),
      valueGetter: (params: any) =>
        params.row.asset?.schemeName || params.row.asset?.name || "-",
    },
    {
      ...extraColumnsField("Weightage (%)", "weightage", 80, 1),
      valueGetter: (params: any) => params.row.weightage || "-",
      align: "right",
      headerAlign: "right",
    },
  ];

  const cumulativeSectorsWithPercentage = (useInstType = false) =>
    sectorsArray(useInstType)?.reduce((acc: any, current: any) => {
      const x = acc.find((item: any) => item.sector === current.sector);
      if (!x) {
        return acc.concat([current]);
      }
      return acc?.map((item: any) =>
        item.sector === current.sector
          ? {
              ...item,
              companyPercentage:
                item.companyPercentage + current.companyPercentage,
            }
          : item,
      );
    }, []);

  // ========================================================================================================================================================================

  const handleNewFn = (capName: string, amountType: any) => {
    const groupedData = _.groupBy(
      allHoldingsInsightsData?.map((data) => {
        return {
          ...data,
          cap: data.asset?.cap,
        };
      }),
      "cap",
    );

    const allCapSum = Object.entries(groupedData)?.map(([key, value]) => {
      return {
        children: value,
        cap: key,
        totalCapValue: _.sum(
          value?.map((item) =>
            amountType === 0 ? item?.currentAmount : item?.investedAmount,
          ),
        ),
        totalCapPercentage:
          (_.sum(
            value?.map((item) =>
              amountType === 0 ? item?.currentAmount : item?.investedAmount,
            ),
          ) /
            _.sum(
              allHoldingsInsightsData?.map((item) =>
                amountType === 0 ? item?.currentAmount : item?.investedAmount,
              ),
            )) *
          100,
        totalCurrentValue: _.sum(
          allHoldingsInsightsData?.map((item) =>
            amountType === 0 ? item?.currentAmount : item?.investedAmount,
          ),
        ),
      };
    });

    return allCapSum
      ?.filter((dataItem) => dataItem?.cap === capName)[0]
      ?.totalCapPercentage?.toFixed(2);
  };

  // ========================================================================================================================================================================

  const totalValue = _.sum(
    allHoldingsInsightsData?.map((data: any) =>
      currentValueTabs === 0 ? data?.currentAmount : data?.investedAmount,
    ),
  );

  const capData = (capSize: string, sector?: string) =>
    allHoldingsInsightsData
      ?.filter((data: any) => {
        if (sector)
          return data.asset.sector === sector && data.asset.cap === capSize;
        return data.asset.cap === capSize;
      })
      .filter(
        (item: any, index: any, self: any) =>
          index ===
          self.findIndex((t: any) => t.asset.name === item.asset.name),
      )
      .map((element: any) => {
        const data = {
          name: element.asset.name,
          color: `#${(0x1000000 + Math.random() * 0xffffff)
            .toString(14)
            .substr(1, 6)}55`,
          loc:
            currentValueTabs === 0
              ? (element.currentAmount / totalValue) * 100
              : (element.investedAmount / totalValue) * 100,
          sector: element.asset.sector,
          capSize,
        };
        return data;
      })
      ?.filter((item: any) => item.loc > 0);

  useMemo(() => {
    if (pieClickData) {
      setSunBurstData({
        name: "sunburst",
        children: [
          {
            name: "Large Cap",
            color: LARGE_CAP_COLOR,
            children: capData("large")
              .sort((a: any, b: any) => b.loc - a.loc)
              .filter((item: any) => item.sector === pieClickData?.id),
          },
          {
            name: "Mid Cap",
            color: MID_CAP_COLOR,
            children: capData("medium")
              .sort((a: any, b: any) => b.loc - a.loc)
              .filter((item: any) => item.sector === pieClickData?.id),
          },
          {
            name: "Small Cap",
            color: SMALL_CAP_COLOR,
            children: capData("small")
              .sort((a: any, b: any) => b.loc - a.loc)
              .filter((item: any) => item.sector === pieClickData?.id),
          },
        ],
      });
      setCapSizes([
        {
          name: "Large Cap",
          value: handleNewFn("large", currentValueTabs),
          color: LARGE_CAP_COLOR,
        },
        {
          name: "Mid Cap",
          value: handleNewFn("medium", currentValueTabs),
          color: MID_CAP_COLOR,
        },
        {
          name: "Small Cap",
          value: handleNewFn("small", currentValueTabs),
          color: SMALL_CAP_COLOR,
        },
      ]);
    }
  }, [allHoldingsInsightsData, pieClickData]);

  const defaultData = () => {
    setSunBurstData({
      name: "GridKey Sunburst",
      children: [
        {
          name: "Large Cap",
          color: LARGE_CAP_COLOR,
          children: capData("large")
            .sort((a: any, b: any) => b.loc - a.loc)
            .map((dataItem, index) => {
              return {
                ...dataItem,
                color: PieChartColors[index < 12 ? index : index % 12],
              };
            }),
        },
        {
          name: "Mid Cap",
          color: MID_CAP_COLOR,
          children: capData("medium")
            .sort((a: any, b: any) => b.loc - a.loc)
            .map((dataItem, index) => {
              return {
                ...dataItem,
                color: PieChartColors[index < 12 ? index : index % 12],
              };
            }),
        },
        {
          name: "Small Cap",
          color: SMALL_CAP_COLOR,
          children: capData("small")
            .sort((a: any, b: any) => b.loc - a.loc)
            .map((dataItem, index) => {
              return {
                ...dataItem,
                color: PieChartColors[index < 12 ? index : index % 12],
              };
            }),
        },
      ],
    });
    // cap sizes
    setCapSizes([
      {
        name: "Large Cap",
        value: handleNewFn("large", currentValueTabs),
        color: LARGE_CAP_COLOR,
      },
      {
        name: "Mid Cap",
        value: handleNewFn("medium", currentValueTabs),
        color: MID_CAP_COLOR,
      },
      {
        name: "Small Cap",
        value: handleNewFn("small", currentValueTabs),
        color: SMALL_CAP_COLOR,
      },
    ]);
  };

  const generatePieData = (useInstType = false) => {
    return cumulativeSectorsWithPercentage(useInstType)
      ?.map((item: any) => {
        const { sector } = item;
        return {
          id: sector,
          value: +item.companyPercentage,
          color: "",
        };
      })
      ?.sort((a: any, b: any) => b.value - a.value)
      ?.map((item: any, index: number) => ({
        ...item,
        color: PieChartColors[index < 12 ? index : index % 12],
      }));
  };

  useMemo(() => {
    // pie data set
    setPieData(generatePieData(false));
    defaultData();
  }, [allHoldingsInsightsData, currentValueTabs === 0]);

  useEffect(() => {
    if (sunBurstRef.current)
      sunBurstRef?.current?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "nearest",
      });
  }, [sunBurstRef?.current]);

  useEffect(() => {
    if (pieRef.current)
      pieRef?.current?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "nearest",
      });
  }, [pieRef?.current]);

  return (
    // <>
    //   {!isLoading && allHoldingsInsightsData?.length !== 0 && (
    //     <Card sx={{ mb: 1, padding: 1 }}>
    //       <Grid2 container>
    //         <Grid2
    //           size={3}
    //           display={"flex"}
    //           justifyContent={"flex-start"}
    //           alignItems={"center"}
    //           pt={3}
    //           pl={2}
    //         >
    //           <Typography
    //             fontWeight={700}
    //             fontSize={20}
    //             color={"var(--primary-900)"}
    //           >
    //             Asset Allocation{" "}
    //           </Typography>
    //         </Grid2>
    //         <Grid2
    //           size={9}
    //           display={"flex"}
    //           justifyContent={"flex-end"}
    //           alignItems={"center"}
    //           pt={3}
    //           pr={2}
    //         >
    //           <IconButton
    //             onClick={() => {
    //               setPieClickData("");
    //               setSunBurstClickData("");
    //               setSunBurstHoverData("");
    //               setPieHoverData("");
    //               setCurrentValueTabs(0);
    //               defaultData();
    //             }}
    //           >
    //             <ReloadIcon style={{ height: 23, width: 23 }} />
    //           </IconButton>
    //           <Divider
    //             orientation="vertical"
    //             style={{ height: "40px", margin: "0 10px" }}
    //           />
    //           <Tabs
    //             className="gridkey"
    //             onChange={(e, val) => {
    //               setCurrentValueTabs(val);
    //             }}
    //             value={currentValueTabs}
    //             variant="standard"
    //           >
    //             <Tab disableRipple label="Current " />
    //             <Tab disableRipple label="Invested" />
    //           </Tabs>
    //         </Grid2>
    //         <Grid2 size={12} py={5} container>
    //           <Grid2 size={6}>
    //             <PieChartSection
    //               pieData={pieData?.filter((data: any) => data?.value !== 0)}
    //               pieClickData={pieClickData}
    //               pieHoverData={pieHoverData}
    //               setPieClickData={setPieClickData}
    //               setPieHoverData={setPieHoverData}
    //               pieRef={pieRef}
    //             />
    //           </Grid2>
    //           <Grid2 size={6}>
    //             <SunburstSection
    //               sunBurstClickData={sunBurstClickData}
    //               sunBurstHoverData={sunBurstHoverData}
    //               setSunBurstClickData={setSunBurstClickData}
    //               setSunBurstHoverData={setSunBurstHoverData}
    //               sunBurstData={sunBurstData}
    //               sunBurstRef={sunBurstRef}
    //               capSizes={capSizes}
    //               pieClickData={pieClickData}
    //             />
    //           </Grid2>
    //         </Grid2>
    //       </Grid2>
    //     </Card>
    //   )}

    //   <GKDataGrid
    //     density="compact"
    //     loading={holdingLoading}
    //     columns={assetsHoldingsColumns}
    //     rows={holdingData?.data?.data || []}
    //     zeroHolding
    //     totalCount={holdingData?.data?.totalCount || 0}
    //     name="insights"
    //     tableName="insights_list"
    //     onRowClick={onRowClick}
    //     pinnedColumns={{
    //       left: ["name"],
    //     }}
    //     exportFile={{
    //       path: "/holdings/selected/",
    //       exportName: "HoldingSummaryList",
    //     }}
    //   />
    // </>
    <Grid2 container size={12}>
      {/* <Grid2 size={12}>
        <Typography fontSize={16} fontWeight={700} color="#333">
          Allocation
        </Typography>
      </Grid2> */}
      {/* <Grid2 size={12} width={"570px"}>
        <Typography fontSize={12} fontWeight={400} color="#333">
          Lorem ipsum dolor sit amet consectetur. Lacus fermentum semper tellus
          quis sollicitudin risus pellentesque ut. Morbi pellentesque mi at
          blandit tellus sit tristique lacinia lectus.
        </Typography>
      </Grid2> */}
      <Grid2 size={12}>
        {!isLoading && allHoldingsInsightsData?.length !== 0 && (
          <Card sx={{ p: 0 }}>
            <Grid2
              sx={{
                p: 1,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                borderBottom: "1px solid",
                borderColor: "#E3E8EF",
              }}
            >
              <Typography fontSize={14} fontWeight={700} color="#333">
                Asset Composition
              </Typography>
              <Grid2
                size={9}
                display={"flex"}
                justifyContent={"flex-end"}
                alignItems={"center"}
              >
                <IconButton
                  onClick={() => {
                    setPieClickData("");
                    setSunBurstClickData("");
                    setSunBurstHoverData("");
                    setPieHoverData("");
                    setCurrentValueTabs(0);
                    defaultData();
                  }}
                >
                  <ReloadIcon style={{ height: 20, width: 20 }} />
                </IconButton>
                <Divider
                  orientation="vertical"
                  style={{ height: "40px", margin: "0 10px" }}
                />
                <Tabs
                  value={currentValueTabs}
                  onChange={(e, val) => {
                    setCurrentValueTabs(val);

                    const anchor = (
                      (e.target as HTMLDivElement).ownerDocument || document
                    ).querySelector("#back-to-top-anchor");

                    if (anchor) {
                      anchor.scrollIntoView({
                        block: "center",
                        behavior: "smooth",
                      });
                    }
                  }}
                  variant="standard"
                  className="light"
                  TabIndicatorProps={{ style: { display: "none" } }}
                >
                  <Tab disableRipple label="Current" />
                  <Tab disableRipple label="Invested" />
                </Tabs>
              </Grid2>
            </Grid2>
            <Grid2 container size={12} py={5}>
              <Grid2 size={4}>
                <PieChartSection
                  title={"Assets"}
                  pieData={generatePieData(true)?.filter(
                    (data: any) => data?.value !== 0,
                  )}
                  pieClickData={pieClickData}
                  pieHoverData={pieHoverData}
                  setPieClickData={setPieClickData}
                  setPieHoverData={setPieHoverData}
                  pieRef={pieRef}
                />
              </Grid2>
              <Grid2 size={4}>
                <PieChartSection
                  title={"Sector"}
                  pieData={pieData?.filter((data: any) => data?.value !== 0)}
                  pieClickData={pieClickData}
                  pieHoverData={pieHoverData}
                  setPieClickData={setPieClickData}
                  setPieHoverData={setPieHoverData}
                  pieRef={pieRef}
                />
              </Grid2>
              <Grid2 size={4}>
                <SunburstSection
                  sunBurstClickData={sunBurstClickData}
                  sunBurstHoverData={sunBurstHoverData}
                  setSunBurstClickData={setSunBurstClickData}
                  setSunBurstHoverData={setSunBurstHoverData}
                  sunBurstData={sunBurstData}
                  sunBurstRef={sunBurstRef}
                  capSizes={capSizes}
                  pieClickData={pieClickData}
                />
              </Grid2>
            </Grid2>
          </Card>
        )}
        {!isLoading && allHoldingsInsightsData?.length === 0 && (
          <NoData
            customImg={
              <img
                loading="lazy"
                alt=""
                src="/LandingPage/portfolio.svg"
                draggable={false}
              />
            }
            additionalComponent={
              <GKButton
                variant="contained"
                startIcon={<FilterIcon />}
                endIcon={<RightIcon />}
                onClick={() => {
                  setIsModalOpen?.(true);
                }}
              >
                Select Portfolios
              </GKButton>
            }
            text="No Insights Data Available"
            subText="Select portfolios to view allocation and holding insights."
          />
        )}
      </Grid2>
      {mfHoldingsData?.length > 0 && (
        <>
          <Grid2 size={12}>
            <Card sx={{ p: 0, mt: 2 }}>
              <Grid2
                sx={{
                  p: 1.5,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  borderBottom: "1px solid",
                  borderColor: "#E3E8EF",
                }}
              >
                <Typography fontSize={14} fontWeight={700} color="#333">
                  MF Scheme Weightage
                </Typography>
              </Grid2>
              <Grid2 container>
                <Grid2 size={8}>
                  <GKDataGrid
                    columns={mfSchemeColumns}
                    rows={mfHoldingsData}
                    name="mfScheme"
                    tableName="mf_scheme"
                    maxHeight={"calc(100vh - 430px)"}
                    density="compact"
                    showSearch={false}
                    showHeaderGrid={false}
                  />
                </Grid2>
                <Grid2
                  size={4}
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                >
                  <MFSchemeWeightage mfHoldingsData={mfHoldingsData} />
                </Grid2>
              </Grid2>
            </Card>
          </Grid2>
          <Grid2 size={12}>
            <PairwiseSchemeOverlap
              mfHoldingsData={mfHoldingsData}
              resetOverlapKey={resetOverlapKey}
            />
          </Grid2>
        </>
      )}
    </Grid2>
  );
};

export default InsigntsPageContent;
